/**
 * Timezone utility functions for consistent datetime handling
 * Handles conversion between local time, UTC, and datetime-local input format
 */

/**
 * Formats a Date object for datetime-local input
 * This preserves the local time as displayed to the user
 * @param date - Date object to format
 * @returns string in format "YYYY-MM-DDTHH:mm"
 */
export const formatDateTimeLocal = (date: Date): string => {
  // Validate input
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.error('Invalid date passed to formatDateTimeLocal:', date);
    date = new Date();
  }

  // Get local time components (NOT UTC components)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parses a datetime-local input value and creates a Date object
 * This treats the input as local time (what the user sees)
 * @param dateTimeLocalValue - string from datetime-local input
 * @returns Date object representing the local time
 */
export const parseDateTimeLocal = (dateTimeLocalValue: string): Date => {
  // datetime-local gives us "YYYY-MM-DDTHH:mm" in local time
  // We need to create a Date object that represents this local time
  // IMPORTANT: We must explicitly parse as local time to avoid browser inconsistencies

  if (!dateTimeLocalValue || typeof dateTimeLocalValue !== 'string') {
    console.error('Invalid datetime-local value:', dateTimeLocalValue);
    return new Date();
  }

  // Split the datetime-local value into components
  const [datePart, timePart] = dateTimeLocalValue.split('T');

  if (!datePart || !timePart) {
    console.error('Invalid datetime-local format (expected YYYY-MM-DDTHH:mm):', dateTimeLocalValue);
    return new Date();
  }

  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
    console.error('Invalid datetime components:', { year, month, day, hours, minutes });
    return new Date();
  }

  // Create Date with explicit local time components (month is 0-indexed)
  // This ensures the Date is created in the user's local timezone, not UTC
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

/**
 * Converts a Date object to ISO string for API transmission
 * This converts local time to UTC for storage
 * @param date - Date object in local time
 * @returns ISO string in UTC
 */
export const toApiDateTime = (date: Date): string => {
  return date.toISOString();
};

/**
 * Converts an API datetime string to a local Date object
 * This converts UTC time from API to local time for display
 * @param apiDateTime - ISO string from API (in UTC)
 * @returns Date object in local time
 */
export const fromApiDateTime = (apiDateTime: string): Date => {
  return new Date(apiDateTime);
};

/**
 * Gets the user's timezone offset in minutes
 * @returns timezone offset in minutes (negative for timezones ahead of UTC)
 */
export const getTimezoneOffset = (): number => {
  return new Date().getTimezoneOffset();
};

/**
 * Gets the user's timezone name (e.g., "Europe/Paris")
 * @returns timezone identifier string
 */
export const getTimezoneName = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Formats a date for display with timezone awareness
 * @param date - Date object to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns formatted date string
 */
export const formatDateWithTimezone = (
  date: Date, 
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
};

/**
 * Debug function to log timezone information
 * Useful for troubleshooting timezone issues
 */
export const logTimezoneInfo = (date: Date, label: string = 'Date'): void => {
  console.group(`🕒 Timezone Debug: ${label}`);
  console.groupEnd();
};
